import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMe,
  listUsers,
  createUser,
  updateUser,
  listMembers,
  listUserTargets,
  createUserTarget,
  updateUserTarget,
  deleteUserTarget,
  ApiError,
  type UserTarget,
} from "@/lib/api";
import { Modal } from "@/components/Modal";
import { Avatar } from "@/components/Avatar";

/** What the profile modal needs — satisfied by both TeamUser and Member. */
interface ProfileLike {
  id: string;
  name: string;
  title: string | null;
  description: string | null;
  avatarUrl: string | null;
  email?: string;
  role?: "admin" | "member";
}

const inputCls =
  "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20";
const labelCls = "mb-1.5 block text-xs font-medium text-text-muted";

function RoleBadge({ role }: { role: "admin" | "member" }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
        role === "admin"
          ? "bg-accent-muted text-accent"
          : "border border-border text-text-secondary"
      }`}
    >
      {role}
    </span>
  );
}

export function TeamPage() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const isAdmin = me?.role === "admin";

  return (
    <div>
      <header className="mb-8">
        <h1 className="page-title">Team</h1>
        <p className="mt-2 text-text-secondary">
          {isAdmin
            ? "Everyone with access to the portal — manage roles and accounts."
            : "Everyone with access to the portal."}
        </p>
      </header>

      {me &&
        (isAdmin ? <AdminTeamList currentUserId={me.id} /> : <MemberDirectory />)}
    </div>
  );
}

/** Read-only directory for non-admins (the users API is admin-only). */
function MemberDirectory() {
  const { data: members, isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: listMembers,
  });
  const [openUser, setOpenUser] = useState<ProfileLike | null>(null);
  return (
    <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
      {isLoading ? (
        <div className="px-4 py-10 text-center text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin" />
        </div>
      ) : (
        <ul className="divide-y divide-border-light">
          {members?.map((m) => (
            <li
              key={m.id}
              role="button"
              tabIndex={0}
              onClick={() => setOpenUser(m)}
              onKeyDown={(e) => e.key === "Enter" && setOpenUser(m)}
              className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-bg-card-hover"
            >
              <Avatar name={m.name} url={m.avatarUrl} size={32} />
              <div className="min-w-0">
                <span className="block truncate text-sm font-medium text-text-heading">
                  {m.name}
                </span>
                {m.title && (
                  <span className="block truncate text-xs text-text-secondary">{m.title}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {openUser && (
        <ProfileModal user={openUser} editable={false} onClose={() => setOpenUser(null)} />
      )}
    </div>
  );
}

/**
 * User profile modal. Opens as a display card for everyone; admins get an
 * Edit button that flips the card into edit mode (job title + description).
 */
function ProfileModal({
  user,
  editable,
  onClose,
}: {
  user: ProfileLike;
  /** Whether the viewer may edit (admin) — viewing always starts read-only. */
  editable: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(user.title ?? "");
  const [description, setDescription] = useState(user.description ?? "");
  // Shown values update immediately after a save, without waiting on refetch.
  const [shown, setShown] = useState({
    title: user.title,
    description: user.description,
  });

  useEffect(() => {
    setTitle(user.title ?? "");
    setDescription(user.description ?? "");
    setShown({ title: user.title, description: user.description });
    setEditing(false);
  }, [user]);

  const save = useMutation({
    mutationFn: () =>
      updateUser(user.id, {
        title: title.trim() || null,
        description: description.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      setShown({ title: title.trim() || null, description: description.trim() || null });
      setEditing(false);
    },
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? "Edit profile" : "Profile"}
      footer={
        editing ? (
          <>
            <button
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
              onClick={() => {
                setTitle(shown.title ?? "");
                setDescription(shown.description ?? "");
                setEditing(false);
              }}
            >
              Cancel
            </button>
            <button
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ color: "var(--color-on-accent)" }}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </>
        ) : editable ? (
          <button
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading"
            onClick={() => setEditing(true)}
          >
            <i className="fa-solid fa-pen mr-1.5 text-xs" />
            Edit
          </button>
        ) : undefined
      }
    >
      {editing ? (
        <>
          <div className="mb-4 flex items-center gap-3">
            <Avatar name={user.name} url={user.avatarUrl} size={48} />
            <div className="min-w-0">
              <span className="block truncate font-display text-base font-semibold text-text-heading">
                {user.name}
              </span>
              {user.email && <p className="truncate text-xs text-text-muted">{user.email}</p>}
            </div>
          </div>
          <label className={labelCls}>Job title</label>
          <input
            className={`${inputCls} mb-3`}
            placeholder="e.g. Head of Production"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
          <label className={labelCls}>Description</label>
          <textarea
            className={inputCls}
            rows={4}
            placeholder="What they do, what to come to them for…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </>
      ) : (
        // Display card: centered, reads like a profile rather than a form.
        <div className="pt-1 text-center">
          <div className="mx-auto mb-3 w-fit">
            <Avatar name={user.name} url={user.avatarUrl} size={72} />
          </div>
          <div className="flex items-center justify-center gap-2">
            <span className="font-display text-lg font-semibold text-text-heading">
              {user.name}
            </span>
            {user.role && <RoleBadge role={user.role} />}
          </div>
          <p className="mt-0.5 text-sm font-medium text-accent">
            {shown.title || <span className="font-normal text-text-muted">No job title yet</span>}
          </p>
          {user.email && (
            <a
              href={`mailto:${user.email}`}
              className="mt-1 inline-block text-xs text-text-muted hover:text-accent"
            >
              <i className="fa-regular fa-envelope mr-1" />
              {user.email}
            </a>
          )}
          <div className="mt-4 border-t border-border-light pt-4 text-left">
            {shown.description ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-text">
                {shown.description}
              </p>
            ) : (
              <p className="text-center text-sm text-text-muted">
                No description yet{editable ? " — use Edit to add one." : "."}
              </p>
            )}
          </div>
          <TargetsSection userId={user.id} canEdit={editable} />
        </div>
      )}
    </Modal>
  );
}

/* ── Targets (description + equity %) — admin-managed, visible to all ──── */

const fmtEquity = (pct: number) =>
  `${Number.isInteger(pct) ? pct : pct.toFixed(2).replace(/\.?0+$/, "")}%`;

function TargetsSection({ userId, canEdit }: { userId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: targets } = useQuery({
    queryKey: ["user-targets", userId],
    queryFn: () => listUserTargets(userId),
  });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<UserTarget | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["user-targets", userId] });
  const remove = useMutation({
    mutationFn: (id: string) => deleteUserTarget(id),
    onSuccess: refresh,
  });

  const totalEquity = (targets ?? []).reduce((s, t) => s + t.equityPct, 0);

  return (
    <div className="mt-4 border-t border-border-light pt-4 text-left">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          <i className="fa-solid fa-bullseye mr-1.5" />
          Targets
          {totalEquity > 0 && (
            <span className="ml-2 normal-case tracking-normal">
              · {fmtEquity(totalEquity)} equity in play
            </span>
          )}
        </span>
        {canEdit && !adding && !editing && (
          <button
            onClick={() => setAdding(true)}
            className="text-xs font-semibold text-accent hover:underline"
          >
            + Add target
          </button>
        )}
      </div>

      {(adding || editing) && (
        <TargetForm
          userId={userId}
          target={editing}
          onDone={() => {
            setAdding(false);
            setEditing(null);
            refresh();
          }}
          onCancel={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}

      {(targets ?? []).length === 0 && !adding ? (
        <p className="text-center text-sm text-text-muted">
          No targets set{canEdit ? " — add one to put equity in play." : "."}
        </p>
      ) : (
        <ul className="space-y-2">
          {(targets ?? []).map((t) =>
            editing?.id === t.id ? null : (
              <li
                key={t.id}
                className="group flex items-start gap-3 rounded-token-md border border-border-light bg-bg px-3 py-2.5"
              >
                <span
                  className="mt-0.5 shrink-0 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-bold text-accent"
                  title="Equity on achievement"
                >
                  {fmtEquity(t.equityPct)}
                </span>
                <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm leading-relaxed text-text">
                  {t.description}
                </p>
                {canEdit && (
                  <span className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={() => setEditing(t)}
                      title="Edit target"
                      className="grid h-6 w-6 place-items-center rounded text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
                    >
                      <i className="fa-solid fa-pen text-[10px]" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("Delete this target?")) remove.mutate(t.id);
                      }}
                      title="Delete target"
                      className="grid h-6 w-6 place-items-center rounded text-text-muted hover:bg-bg-card-hover hover:text-danger"
                    >
                      <i className="fa-solid fa-trash-can text-[10px]" />
                    </button>
                  </span>
                )}
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}

function TargetForm({
  userId,
  target,
  onDone,
  onCancel,
}: {
  userId: string;
  /** null → create; otherwise edit this target. */
  target: UserTarget | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState(target?.description ?? "");
  const [equity, setEquity] = useState(target ? String(target.equityPct) : "");
  const save = useMutation({
    mutationFn: () => {
      const equityPct = Number(equity);
      return target
        ? updateUserTarget(target.id, { description: description.trim(), equityPct })
        : createUserTarget(userId, { description: description.trim(), equityPct });
    },
    onSuccess: onDone,
  });
  const equityNum = Number(equity);
  const valid =
    description.trim().length > 0 &&
    equity.trim() !== "" &&
    Number.isFinite(equityNum) &&
    equityNum >= 0 &&
    equityNum <= 100;

  return (
    <div className="mb-3 rounded-token-md border border-border bg-bg p-3">
      <textarea
        className={inputCls}
        rows={2}
        placeholder="e.g. Sign 3 enterprise customers by Q4"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        autoFocus
      />
      <div className="mt-2 flex items-center gap-2">
        <div className="relative w-32">
          <input
            className={`${inputCls} pr-7`}
            placeholder="0.5"
            inputMode="decimal"
            value={equity}
            onChange={(e) => setEquity(e.target.value)}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-muted">
            %
          </span>
        </div>
        <span className="text-xs text-text-muted">equity on achievement</span>
        <span className="flex-1" />
        <button
          onClick={onCancel}
          className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
        >
          Cancel
        </button>
        <button
          onClick={() => save.mutate()}
          disabled={!valid || save.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ color: "var(--color-on-accent)" }}
        >
          {save.isPending ? "Saving…" : target ? "Save" : "Add target"}
        </button>
      </div>
      {save.isError && (
        <p className="mt-2 text-xs text-danger">
          Could not save target — check the values and try again.
        </p>
      )}
    </div>
  );
}

function AdminTeamList({ currentUserId }: { currentUserId: string }) {
  const qc = useQueryClient();
  const { data: users, isLoading } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const [addOpen, setAddOpen] = useState(false);
  const [openUser, setOpenUser] = useState<ProfileLike | null>(null);

  const mutate = useMutation({
    mutationFn: (vars: { id: string; fields: Parameters<typeof updateUser>[1] }) =>
      updateUser(vars.id, vars.fields),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Members{users ? ` · ${users.length}` : ""}
        </h2>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded-token-md bg-accent text-on-accent px-3 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-plus mr-1.5 text-xs" />
          Add user
        </button>
      </div>

      <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
        {isLoading ? (
          <div className="px-4 py-10 text-center text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin" />
          </div>
        ) : (
          <ul className="divide-y divide-border-light">
            {users?.map((u) => {
              const isSelf = u.id === currentUserId;
              const pending = mutate.isPending && mutate.variables?.id === u.id;
              return (
                <li
                  key={u.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenUser(u)}
                  onKeyDown={(e) => e.key === "Enter" && setOpenUser(u)}
                  className="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-bg-card-hover"
                >
                  <Avatar name={u.name} url={u.avatarUrl} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-text-heading">
                        {u.name}
                      </span>
                      {u.title && (
                        <span className="truncate text-xs text-text-secondary">
                          {u.title}
                        </span>
                      )}
                      {isSelf && <span className="text-xs text-text-muted">(you)</span>}
                    </div>
                    <span className="truncate text-xs text-text-muted">{u.email}</span>
                  </div>

                  <RoleBadge role={u.role} />

                  <span className="flex w-20 items-center gap-1.5 text-xs">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{
                        background: u.active
                          ? "var(--color-success)"
                          : "var(--color-text-muted)",
                      }}
                    />
                    <span className={u.active ? "text-text-secondary" : "text-text-muted"}>
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </span>

                  <div
                    onClick={(e) => e.stopPropagation()}
                    className={`flex w-44 justify-end gap-1.5 transition-opacity ${
                      isSelf
                        ? "invisible"
                        : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
                    }`}
                  >
                    {!isSelf && (
                      <>
                        <button
                          disabled={pending}
                          onClick={() =>
                            mutate.mutate({
                              id: u.id,
                              fields: { role: u.role === "admin" ? "member" : "admin" },
                            })
                          }
                          className="rounded-md px-2 py-1 text-xs text-text-muted hover:bg-bg-elevated hover:text-text-heading disabled:opacity-50"
                        >
                          {u.role === "admin" ? "Make member" : "Make admin"}
                        </button>
                        <button
                          disabled={pending}
                          onClick={() =>
                            mutate.mutate({ id: u.id, fields: { active: !u.active } })
                          }
                          className="rounded-md px-2 py-1 text-xs text-text-muted hover:bg-bg-elevated hover:text-text-heading disabled:opacity-50"
                        >
                          {u.active ? "Deactivate" : "Reactivate"}
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AddUserModal open={addOpen} onClose={() => setAddOpen(false)} />
      {openUser && (
        <ProfileModal user={openUser} editable onClose={() => setOpenUser(null)} />
      )}
    </section>
  );
}

function AddUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [title, setTitle] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createUser({ name, email, password, title: title.trim() || undefined, role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setName("");
      setEmail("");
      setPassword("");
      setTitle("");
      setRole("member");
      setError(null);
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not create user"),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add user">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        <div className="mb-3">
          <label className={labelCls}>Name</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="mb-3">
          <label className={labelCls}>Email</label>
          <input
            type="email"
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="mb-3">
          <label className={labelCls}>Job title (optional)</label>
          <input
            className={inputCls}
            placeholder="e.g. Head of Production"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className={labelCls}>Temporary password (min 8 chars)</label>
          <input
            type="text"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
          <p className="mt-1 text-xs text-text-muted">
            Share this with the user. They can change it under Settings.
          </p>
        </div>
        <div className="mb-4">
          <label className={labelCls}>Role</label>
          <select
            className={inputCls}
            value={role}
            onChange={(e) => setRole(e.target.value as "admin" | "member")}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        {error && (
          <p className="mb-3 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
          >
            {create.isPending ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
